import { PayloadAction } from "@reduxjs/toolkit";
import { eventChannel, END } from "redux-saga";
import { takeLatest, all, call, put, take, fork } from "redux-saga/effects";

import { IProgressState } from "infrastructure/helpers/common";
import { HttpError, HttpStatusCodes } from "infrastructure/http";
import { notification } from "infrastructure/notification";
import { createNotificationResult } from "infrastructure/store";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { translationUploadActions } from "./TranslationUpload.actions";
import { translationUploadApi as api } from "./TranslationUpload.api";
import { getValidationIssues } from "./TranslationUpload.helpers";

const createUploadChannel = (file: File) => {
  let emit;

  const channel = eventChannel(emitter => {
    emit = emitter;

    return () => {};
  });

  const promise = call(api.uploadTranslation, file, (progress: IProgressState) => {
    emit(progress.value);

    if (progress.completed) {
      emit(END);
    }
  });

  return [promise, channel];
};

function* progressListener(channel) {
  while (true) {
    const data = yield take(channel);

    yield put(translationUploadActions.uploadTranslationProcessing(data));
  }
}

function* uploadTranslationExecute(action: PayloadAction<File[]>) {
  try {
    const file = action.payload[0];

    if (file != null) {
      const [promise, channel] = createUploadChannel(file);
      yield fork(progressListener, channel);

      yield promise;

      yield put(translationUploadActions.uploadTranslationCompleted());
    } else {
      yield put(translationUploadActions.uploadTranslationError(notification.error().useMessageId(displayMessages.error("translationUploadFileRequired"))));
    }
  } catch (error) {
    if (error instanceof HttpError) {
      if (error.statusCode === HttpStatusCodes.BadRequest) {
        yield put(
          translationUploadActions.uploadTranslationFailed(
            createNotificationResult({
              validationIssues: getValidationIssues(error.data),
              text: error.message
            }).withNotification(notification.error().useMessageId(displayMessages.error("messagesUpload")))
          )
        );
      }
    } else {
      yield put(translationUploadActions.uploadTranslationError(notification.error().useMessageId(displayMessages.error("translationUploadUnexpected"))));
    }
  }
}

function* actionWatcher() {
  yield takeLatest(translationUploadActions.uploadTranslation.toString(), uploadTranslationExecute);
}

export function* translationUploadSaga() {
  yield all([actionWatcher()]);
}
